import { useEffect, useMemo, useState } from "react";
import { getPurchaseOrders } from "../../services/api/boq/purchaseService";
import { FaSearch, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { getVendors } from "../../services/api/boq/VendorService";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  Eye,  
  Pencil,
  Trash2,
  Plus,
  X,
  DollarSign,
  CheckCircle,
  Clock,
  Package,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getPurchaseOrderById } from "../../services/api/boq/purchaseService";
import { toWords } from "number-to-words";
import toast from "react-hot-toast";

export default function PurchaseOrderList() {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedPO, setSelectedPO] = useState(null);
  const [sortBy, setSortBy] = useState("date_desc");
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  useEffect(() => {
    loadPOs();
    loadVendors();
    loadProjects();
    loadClients();
    loadSummary();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, projectFilter]);

  const loadSummary = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/purchase-orders/po-summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const result = await response.json();
      //setSummary(data); // Assuming API returns { total_amount, approved_amount, etc. }
      if (result.status) {
        setSummary(result.data);
      }
    } catch (error) {
      console.error("Failed to load summary", error);
    }
  };

  const loadPOs = async () => {
    try {
      const res = await getPurchaseOrders();
      setPurchaseOrders(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (e) {
      console.error(e);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };
  // NEW: Fetch full PO details (with items) before showing modal
  // const handleViewPO = async (po) => {
  //   try {
  //     const res = await getPurchaseOrderById(po.id);
  //     setSelectedPO(res.data); // Modal opens with item details
  //   } catch (error) {
  //     toast.error("Failed to fetch items for this PO");
  //   }
  // };
  const handleViewPO = async (po, mode = "view") => {
    try {
      const res = await getPurchaseOrderById(po.id);
      // We spread the data and add an 'isSubmitMode' flag
      setSelectedPO({
        ...res.data,
        isSubmitMode: mode === "submit",
      });
    } catch (error) {
      toast.error("Failed to fetch items for this PO");
    }
  };

  const handleSubmitPO = async (id) => {
    // 1. Show a loading indicator immediately
    const toastId = toast.loading("Updating status and sending email...");

    try {
      const baseUrl = process.env.REACT_APP_API_URL;

      // 2. IMMEDIATE UI UPDATE (Optimistic)
      // We update the local list so the "Draft" button turns into "Pending" right now
      setPurchaseOrders((prevOrders) =>
        prevOrders.map((po) =>
          po.id === id ? { ...po, status: "pending" } : po,
        ),
      );

      // 3. Close the modal so the user sees the table change
      setSelectedPO(null);

      // 4. Now call the actual backend API
      const response = await fetch(`${baseUrl}/purchase-orders/${id}/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        toast.success("PO Submitted! Status is now Pending.", { id: toastId });
        // Final sync with DB to ensure everything is correct
        await loadPOs();
      } else {
        // ROLLBACK: If API fails (e.g. email error), revert back to Draft
        await loadPOs();
        const errorData = await response.json().catch(() => ({}));
        toast.error(
          errorData.message || "Failed to submit. Reverting to Draft.",
          { id: toastId },
        );
      }
    } catch (error) {
      // ROLLBACK on network error
      await loadPOs();
      console.error("Submission error:", error);
      toast.error("Network Error: Status reverted to Draft", { id: toastId });
    }
  };

  const loadVendors = async () => {
    let allVendors = [];
    let page = 1;
    let lastPage = 1;

    do {
      const res = await getVendors(page);
      const response = res?.data?.data;

      allVendors = [...allVendors, ...(response?.data || [])];
      lastPage = response?.last_page || 1;

      page++;
    } while (page <= lastPage);

    setVendors(allVendors);
  };

  const loadProjects = async () => {
    const res = await fetchProjectsApi();
    setProjects(res?.data?.data?.data || []);
  };

  const loadClients = async () => {
    const res = await fetchClientsApi();
    setClients(res?.data || []);
  };

  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach((v) => (map[v.id] = v.name));
    return map;
  }, [vendors]);

  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach((p) => (map[p.id] = p.project_name));
    return map;
  }, [projects]);

  /* ---------------- FILTER ---------------- */
  const filteredPOs = useMemo(() => {
    if (!Array.isArray(purchaseOrders)) return [];
    return purchaseOrders.filter((po) =>
      po.po_number.toLowerCase().includes(search.toLowerCase()),
    );
  }, [purchaseOrders, search, statusFilter]);

  /* ---------------- EXPORT ---------------- */
  const exportExcel = () => {
    const data = filteredOrders.map((po) => ({
      "PO No": po.po_number,
      Date: po.order_date,
      Amount: po.total_amount,
      Status: po.status,
      Vendor: vendorMap[po.vendor_id] || "N/A",
      Project: projectMap[po.project_id] || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchase Orders");
    XLSX.writeFile(wb, "purchase_orders.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["PO No", "Date", "Amount", "Status", "Vendor", "Project"]],
      body: filteredPOs.map((po) => [
        po.po_number,
        po.order_date,
        po.total_amount,
        po.status,
        vendorMap[po.vendor_id] || "N/A",
        projectMap[po.project_id] || "N/A",
      ]),
    });
    doc.save("purchase_orders.pdf");
  };

  const filteredOrders = purchaseOrders
    .filter((po) => {
      const q = search.toLowerCase();

      const matchesSearch =
        po.po_number?.toLowerCase().includes(q) ||
        String(po.vendor_id).includes(q) ||
        String(po.project_id).includes(q);

      const matchesStatus = statusFilter ? po.status === statusFilter : true;

      const matchesProject = projectFilter
        ? String(po.project_id) === projectFilter
        : true;

      return matchesSearch && matchesStatus && matchesProject;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.order_date) - new Date(b.order_date);

        case "amount_desc":
          return Number(b.total_amount) - Number(a.total_amount);

        case "amount_asc":
          return Number(a.total_amount) - Number(b.total_amount);

        default:
          return new Date(b.order_date) - new Date(a.order_date);
      }
    });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Helper to convert number to words (for PDF)
  function numberToWords(num) {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000)
        return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000)
        return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000)
        return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return (
        inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000)
      );
    };

    return inWords(Math.floor(num)) + " Only";
  }

  const generatePOPDF = async (id) => {
    try {
      const poRes = await getPurchaseOrderById(id);
      const vendorRes = await getVendors();
      const projectRes = await fetchProjectsApi();
      const clientRes = await fetchClientsApi();

      const po = poRes.data;
      const projects = projectRes.data.data.data || [];

      const vendor = vendors.find((v) => v.id === po.vendor_id) || {};
      const project = projects.find((p) => p.id === po.project_id) || {};
      const client = clients.find((c) => c.id === project.client_id) || {};

      const doc = new jsPDF("p", "mm", "a4");

      const pageWidth = 210;
      const pageHeight = 297;

      // ======================================
      // HEADER
      // ======================================

      const logo = "/logo_htcl.png";

      doc.addImage(logo, "PNG", 8, 10, 18, 10);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);

      doc.text("Ketan Ventures Group", 105, 15, {
        align: "center",
      });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);

      const companyLines = [
        "5N201, 2ED Floor, Provident Sunworth",
        "Phase- 2, Venkatpura, Bangalore- 560060",
        "Karnataka, India",
      ];

      let headerY = 20;

      companyLines.forEach((line) => {
        doc.text(line, 105, headerY, {
          align: "center",
        });

        headerY += 4;
      });

      // Divider
      doc.line(5, 38, 205, 38);

      // ======================================
      // TITLE
      // ======================================

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);

      doc.text("PURCHASE ORDER", 105, 45, {
        align: "center",
      });

      // ======================================
      // PARTY DETAILS SECTION
      // ======================================

      let topY = 50;

      // RIGHT SECTION HEIGHT
      const rightSectionHeight = 78;

      // ======================================
      // VENDOR DETAILS
      // ======================================

      const vendorText = [
        vendor.name || "-",
        vendor.address || "-",
        `GSTIN/UIN : ${vendor.gst_number || "-"}`,
        `Phone : ${vendor.mobile || "-"}`,
        `Email : ${vendor.email || "-"}`,
      ];

      // Dynamic Height
      let vendorHeight = 4;

      vendorText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        vendorHeight += split.length * 4;
      });

      vendorHeight += 6;

      // Vendor Box
      doc.rect(5, topY, 125, vendorHeight);

      // ======================================
      // CONSIGNEE DETAILS
      // ======================================

      const consigneeText = [
        client.name || "-",
        client.in_site_location || "-",
        `GSTIN/UIN : ${client.gst_no || "-"}`,
        `State Name : Gujarat, Code : 24`,
      ];

      let consigneeHeight = 4;

      consigneeText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        consigneeHeight += split.length * 4;
      });

      consigneeHeight += 6;

      const consigneeY = topY + vendorHeight;

      // Consignee Box
      doc.rect(5, consigneeY, 125, consigneeHeight);

      // ======================================
      // BUYER DETAILS
      // ======================================

      const buyerText = [
        client.name || "-",
        client.in_site_location || "-",
        `GSTIN/UIN : ${client.gst_no || "-"}`,
        `State Name : Gujarat, Code : 24`,
      ];

      let buyerHeight = 4;

      buyerText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        buyerHeight += split.length * 4;
      });

      buyerHeight += 6;

      const buyerYPos = consigneeY + consigneeHeight;

      // Buyer Box
      doc.rect(5, buyerYPos, 125, buyerHeight);

      // ======================================
      // RIGHT DETAILS BOX
      // ======================================

      // Total Right Height
      const totalLeftHeight =
        vendorHeight + consigneeHeight + buyerHeight;

      const finalRightHeight = Math.max(
        totalLeftHeight,
        rightSectionHeight
      );

      // Right Box
      doc.rect(130, topY, 75, finalRightHeight);

      // ======================================
      // VENDOR CONTENT
      // ======================================

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);

      doc.text("Vendor (Supplier)", 8, topY + 5);

      doc.setFont("helvetica", "normal");

      let vendorY = topY + 10;

      vendorText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        doc.text(split, 8, vendorY);

        vendorY += split.length * 4;
      });

      // ======================================
      // CONSIGNEE CONTENT
      // ======================================

      doc.setFont("helvetica", "bold");

      doc.text("Consignee (Ship to)", 8, consigneeY + 5);

      doc.setFont("helvetica", "normal");

      let consigneeTextY = consigneeY + 10;

      consigneeText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        doc.text(split, 8, consigneeTextY);

        consigneeTextY += split.length * 4;
      });

      // ======================================
      // BUYER CONTENT
      // ======================================

      doc.setFont("helvetica", "bold");

      doc.text("Buyer (Bill to)", 8, buyerYPos + 5);

      doc.setFont("helvetica", "normal");

      let buyerTextY = buyerYPos + 10;

      buyerText.forEach((line) => {
        const split = doc.splitTextToSize(line, 110);

        doc.text(split, 8, buyerTextY);

        buyerTextY += split.length * 4;
      });

      // ======================================
      // RIGHT SIDE DETAILS
      // ======================================

      const rightDetails = [
        ["Voucher No.", po.po_number || "-"],
        ["Dated", po.order_date || "-"],
        ["Project Ref No.", project.project_code || "-"],
        ["Dispatch Through", "-"],
        ["Destination", po.deliver_to || "-"],
        ["Terms of Delivery", "-"],
      ];

      let rightY = topY + 8;

      rightDetails.forEach((item) => {
        doc.setFont("helvetica", "bold");

        doc.text(item[0], 135, rightY);

        doc.setFont("helvetica", "normal");

        const split = doc.splitTextToSize(item[1], 58);

        doc.text(split, 135, rightY + 4);

        rightY += split.length * 4 + 6;
      });

      // ======================================
      // TABLE
      // ======================================

      const tableRows = po.items.map((item, index) => [
        index + 1,
        item.item_name || "-",
        `${item.ordered_qty || 0}`,
        formatCurrency(item.unit_price || 0),
        item.unit || "PCS",
        formatCurrency(item.total || 0),
      ]);

      const totalAmount = po.items.reduce(
        (sum, item) => sum + parseFloat(item.total || 0),
        0
      );

      const gstAmount = parseFloat(po.gst_amount || 0);

      const grandTotal = totalAmount + gstAmount;

      const amountWords = numberToWords(grandTotal);

      const tableStartY = Math.max(
        topY + finalRightHeight,
        buyerYPos + buyerHeight
      );

      autoTable(doc, {
        startY: tableStartY,

        margin: {
          left: 5,
          right: 5,
        },

        tableWidth: 190,

        head: [[
          "Sl",
          "Description of Goods",
          "Qty",
          "Rate",
          "Per",
          "Amount",
        ]],

        body: tableRows,

        theme: "grid",

        styles: {
          fontSize: 9,
          cellPadding: 1.8,
          lineWidth: 0.3,
          lineColor: [0, 0, 0],
          overflow: "linebreak",
          valign: "middle",
          textColor: [0, 0, 0],
        },

        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 10,
            halign: "center",
          },
          1: {
            cellWidth: 109,
          },
          2: {
            cellWidth: 16,
            halign: "left",
          },
          3: {
            cellWidth: 20,
            halign: "left",
          },
          4: {
            cellWidth: 18,
            halign: "left",
          },
          5: {
            cellWidth: 27,
            halign: "left",
          },
        },

        didDrawPage: function () {
          // Border on Every Page
          doc.setDrawColor(0);
          doc.setLineWidth(0.3);
          doc.rect(5, 5, 200, 287);

          // Footer on Every Page
          doc.setFontSize(8);

          doc.text(
            "This is a Computer Generated Document",
            pageWidth / 2,
            290,
            {
              align: "center",
            }
          );
        },
      });

      // ======================================
      // TOTAL SECTION
      // ======================================

      let finalY = doc.lastAutoTable.finalY;

      // Multi Page Safety
      if (finalY > 240) {
        doc.addPage();
        finalY = 20;

        // Border
        doc.rect(5, 5, 200, 287);

        // Footer
        doc.setFontSize(8);

        doc.text(
          "This is a Computer Generated Document",
          pageWidth / 2,
          290,
          {
            align: "center",
          }
        );
      }

      // Totals Box
      doc.rect(5, finalY, 200, 22);

      doc.line(140, finalY, 140, finalY + 22);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);

      doc.text(
        `Total ${formatCurrency(grandTotal)}`,
        198,
        finalY + 8,
        {
          align: "right",
        }
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);

      doc.text("Amount Chargeable (in words)", 8, finalY + 7);

      const amountLines = doc.splitTextToSize(
        `INR ${amountWords}`,
        120
      );

      doc.text(amountLines, 8, finalY + 13);

      // ======================================
      // REMARKS + SIGNATURE
      // ======================================

      let remarksY = finalY + 28;

      // Outer Box
      doc.rect(5, remarksY, 200, 40);

      // Vertical Divider
      doc.line(135, remarksY, 135, remarksY + 40);

      // LEFT SIDE
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);

      doc.text("Remarks:", 8, remarksY + 6);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      const remarksText = [
        `1. Notes: ${po.notes || "All amounts are in INR"}`,
        `2. Terms & Conditions: ${po.t_c || "No terms specified"}`,
      ];

      let remarksTextY = remarksY + 12;

      remarksText.forEach((line) => {
        const split = doc.splitTextToSize(line, 120);

        doc.text(split, 8, remarksTextY);

        remarksTextY += split.length * 5 + 2;
      });

      // RIGHT SIDE SIGNATURE
      doc.setFont("helvetica", "normal");

      doc.text(
        "for Ketan Ventures Group",
        145,
        remarksY + 10
      );

      const signImg = "/sign.png"; // Replace with your signature image path

      doc.addImage(
        signImg,
        "PNG",
        150,
        remarksY + 13,
        30,
        10
      );

      doc.text(
        "Authorised Signatory",
        148,
        remarksY + 30
      );

      // ======================================
      // FINAL BORDER + FOOTER ALL PAGES
      // ======================================

      const totalPages = doc.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Border
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);

        doc.rect(5, 5, 200, 287);

        // Footer
        doc.setFontSize(8);

        doc.text(
          "This is a Computer Generated Document",
          pageWidth / 2,
          290,
          {
            align: "center",
          }
        );
      }

      // ======================================
      // SAVE
      // ======================================

      doc.save(`PO_${po.po_number}.pdf`);

    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  const totalAmountByProject = filteredOrders.reduce((acc, po) => {
    const projectName = projectMap[po.project_id] || "Unknown";

    acc[projectName] = (acc[projectName] || 0) + Number(po.total_amount);

    return acc;
  }, {});

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="p-4 space-y-4">
      {/* 📊 SUMMARY CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          title="Total PO Amount"
          amount={summary?.total_po_amount}
          icon={<DollarSign className="text-blue-600" />}
          bgColor="bg-blue-50"
          borderColor="border-blue-500"
        />
        <SummaryCard
          title="Approved Amount"
          amount={summary?.approved_amount}
          icon={<CheckCircle className="text-green-600" />}
          bgColor="bg-green-50"
          borderColor="border-green-500"
        />
        <SummaryCard
          title="Pending Amount"
          amount={summary?.pending_amount}
          icon={<Clock className="text-yellow-600" />}
          bgColor="bg-yellow-50"
          borderColor="border-yellow-500"
        />
        <SummaryCard
          title="Received Amount"
          amount={summary?.received_amount}
          icon={<Package className="text-purple-600" />}
          bgColor="bg-purple-50"
          borderColor="border-purple-500"
        />
      </div>
      {/* 🔍 FILTER BAR */}
      <div className="bg-white shadow-sm border rounded-xl p-4 flex flex-col gap-4">
        {/* TOP ROW - Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* LEFT: Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full">
            {/* Search */}
            <div className="relative w-full sm:w-60">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Search PO..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Project Filter */}
            <select
              className="w-full sm:w-48 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={projectFilter}
              onChange={(e) => {
                setProjectFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.project_name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              className="w-full sm:w-40 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            {/* Show Entries */}
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-gray-50">
              <span className="text-sm text-gray-600">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-transparent focus:outline-none text-sm"
              >
                {[10, 50, 100, 250, 500].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* RIGHT: Actions */}
          {/* RIGHT: Actions (Single Line) */}
          <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full lg:w-auto lg:justify-end">
            <button
              onClick={() => navigate("/purchase-orders/create")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition whitespace-nowrap"
            >
              Create PO
            </button>

            <button
              onClick={exportExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition whitespace-nowrap"
            >
              <FaFileExcel />
              Excel
            </button>

            <button
              onClick={exportPDF}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition whitespace-nowrap"
            >
              <FaFilePdf />
              PDF
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white p-4 rounded shadow border">
        <h3 className="font-semibold mb-2">Project Wise Total Amount</h3>

        <div className="grid md:grid-cols-3 gap-2 text-sm">
          {Object.entries(totalAmountByProject).map(([project, amount]) => (
            <div key={project} className="border rounded p-2">
              <strong>{project}</strong>
              <p>₹{formatCurrency(amount)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 🖥 DESKTOP TABLE */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading purchase orders...
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-2 text-left font-medium">Sl No</th>
                <th className="p-3 text-left font-medium">PO No</th>
                <th className="p-3 text-left font-medium">Vendor</th>
                <th className="p-3 text-left font-medium">Project</th>
                <th className="p-3 text-center font-medium">Items</th>
                <th className="p-3 text-left font-medium">Order Date</th>
                <th className="p-3 text-left font-medium">Total Amount</th>
                <th className="p-3 text-left font-medium">GST Amount</th>
                <th className="p-3 text-left font-medium">Excepted Delivery</th>
                <th className="p-3 text-left font-medium">Approval Status</th>
                <th className="p-3 text-center font-medium">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-gray-500">
                    No purchase orders found
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((po, index) => (
                  <tr
                    key={po.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition text-left"
                  >
                    <td className="p-2 font-medium">{index + 1}</td>
                    <td className="p-3 font-medium">{po.po_number}</td>
                    <td className="p-3 text-gray-600">
                      {vendorMap[po.vendor_id] || "-"}
                    </td>
                    <td className="p-3 text-gray-600">
                      {projectMap[po.project_id] || "-"}
                    </td>
                    <td className="p-3 ">{po.items_count || 0}</td>
                    <td className="p-3 text-gray-600">{po.order_date}</td>
                    <td className="p-3 font-semibold">
                      <span className="text-xl mr-1">₹</span>
                      {formatCurrency(po.total_amount)}
                    </td>

                    <td className="p-3">
                      <span className="text-xl mr-1">₹</span>
                      {formatCurrency(po.gst_amount)}
                    </td>
                    <td className="p-3 text-gray-600">
                      {po.delivery_date || "-"}
                    </td>
                    {/* <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          po.status === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {po.status}
                      </span>
                    </td> */}
                    {/* <td className="p-3 text-center">
                      {po.status === "draft" ? (
                        <div className="relative group inline-block">
                          <button
                            onClick={() => handleViewPO(po, "submit")} // Pass 'submit' mode
                            className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition cursor-pointer"
                          >
                            Draft
                          </button>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-32 bg-black text-white text-[10px] text-center py-1 rounded shadow-lg z-[100]">
                            Click to Submit & Send Mail
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                          </div>
                        </div>
                      ) : po.status === "pending" ? (
                        <button
                          onClick={() => navigate(`/purchase-orders/${po.id}`)}
                          className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition border border-yellow-300"
                          title="Click to Review & Approve"
                        >
                          Pending
                        </button>
                      ) : (
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full border ${
                            po.status === "approved" ||
                            po.status === "completed"
                              ? "bg-green-100 text-green-700 border-green-300"
                              : po.status === "partial completed"
                                ? "bg-purple-100 text-purple-700 border-purple-300"
                                : "bg-red-100 text-red-700 border-red-300"
                          }`}
                        >
                          {po.status}
                        </span>
                      )}
                    </td> */}
                    <td className="p-3 text-center">
                      {po.status === "draft" ? (
                        <div className="relative group inline-block">
                          <button
                            onClick={() => handleViewPO(po, "submit")} // Pass 'submit' mode
                            className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition cursor-pointer"
                          >
                            Draft
                          </button>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-32 bg-black text-white text-[10px] text-center py-1 rounded shadow-lg z-[100]">
                            Click to Submit & Send Mail
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                          </div>
                        </div>
                      ) : po.status === "pending" ? (
                        <button
                          onClick={() => navigate(`/purchase-orders/${po.id}`)}
                          className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition border border-yellow-300"
                          title="Click to Review & Approve"
                        >
                          Pending
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            if (po.status === "approved") {
                              navigate(`/dc-in?po_id=${po.id}`);
                            }
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${po.status === "approved" ||
                            po.status === "completed"
                            ? "bg-green-100 text-green-700 border-green-300 hover:bg-green-600 hover:text-white cursor-pointer"
                            : po.status === "partial completed"
                              ? "bg-purple-100 text-purple-700 border-purple-300"
                              : "bg-red-100 text-red-700 border-red-300"
                            }`}
                          title={
                            po.status === "approved"
                              ? "Click to Create DC-IN"
                              : ""
                          }
                        >
                          {po.status}
                        </button>
                      )}
                    </td>
                    {/* <td className="p-3">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => navigate(`/po/${po.id}/payments`)}
                          className="text-green-600 hover:text-green-800 text-xl font-bold"
                          title="Pay"
                        >
                          ₹
                        </button>
                        <button
                          onClick={() => handleViewPO(po, "view")} // Pass 'view' mode
                          className="text-blue-600 hover:text-blue-800"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => generatePOPDF(po.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaFilePdf size={18} />
                        </button>
                      </div>
                    </td> */}
                    <td className="p-3">
                      <div className="flex justify-start gap-3">
                        {/* 1. DRAFT ONLY: Edit Button */}
                        {(po.status === "draft" ||
                          po.status === "pending" ||
                          po.status === "approved") && (
                            <button
                              onClick={() =>
                                navigate(`/purchase-orders/edit/${po.id}`)
                              }
                              className="text-amber-500 hover:text-amber-700 transition-colors"
                              title="Edit Draft"
                            >
                              <Pencil size={18} />
                            </button>
                          )}
                        {/* PAY BUTTON - Only show if status is NOT draft */}
                        {po.status !== "draft" && (
                          <button
                            onClick={() => navigate(`/po/${po.id}/payments`)}
                            className="text-xl font-bold text-green-600 hover:text-green-800 transition-colors"
                            title="Pay"
                          >
                            ₹
                          </button>
                        )}

                        {/* VIEW BUTTON (Always enabled) */}
                        <button
                          onClick={() => handleViewPO(po, "view")}
                          className="text-blue-600 hover:text-blue-800"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>

                        {/* PDF BUTTON */}
                        <button
                          disabled={po.status === "draft"}
                          onClick={() =>
                            po.status !== "draft" && generatePOPDF(po.id)
                          }
                          className={`transition-colors ${po.status === "draft"
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-red-600 hover:text-red-800"
                            }`}
                          title={
                            po.status === "draft"
                              ? "PDF available after submission"
                              : "Download PDF"
                          }
                        >
                          <FaFilePdf size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        <div className="flex justify-center gap-2 mt-4 flex-wrap">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          <span className="px-3 py-1">
            {currentPage} / {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* 📱 MOBILE CARDS */}
      <div className="grid gap-4 md:hidden">
        {paginatedOrders.map((po) => (
          <div
            key={po.id}
            onClick={() => setSelectedPO(po)}
            className="border rounded-xl p-4 shadow-sm cursor-pointer"
          >
            <div className="flex justify-between">
              <h3 className="font-bold">{po.po_number}</h3>
              <span className="text-sm bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                {po.status}
              </span>
            </div>
            <p className="text-sm">Items: {po.items?.length || 0}</p>
            <p className="text-sm">Project: {projectMap[po.project_id]}</p>
            <p className="text-sm mt-1">Date: {po.order_date}</p>
            <p className="text-sm">
              Amount: ₹{formatCurrency(po.total_amount)}
            </p>
            <p className="mt-2 text-right text-blue-600 font-medium">
              Tap to view →
            </p>
          </div>
        ))}
      </div>

      {/* 🔍 VIEW MODAL */}
      {/* Modal */}
      {selectedPO && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">PO Preview</h2>

            <div className="space-y-2 text-sm">
              <p>
                <strong>PO No:</strong> {selectedPO.po_number}
              </p>
              <p>
                <strong>Date:</strong> {selectedPO.order_date}
              </p>
              <p>
                <strong>Amount:</strong> ₹
                {formatCurrency(selectedPO.total_amount)}
              </p>
              <p>
                <strong>Status:</strong> {selectedPO.status}
              </p>
            </div>
            {/* ============================================================ */}
            {/* ADD THE NEW ITEMS TABLE HERE: */}
            {/* ============================================================ */}
            <div className="mb-8">
              <h3 className="font-bold text-gray-700 mb-3 border-l-4 border-blue-500 pl-2">
                Ordered Items
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="p-3 text-left">Item Name</th>
                        <th className="p-3 text-center">Qty</th>
                        <th className="p-3 text-right">Rate</th>
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPO.items?.length > 0 ? (
                        selectedPO.items.map((item, idx) => (
                          <tr
                            key={idx}
                            className="border-b last:border-0 hover:bg-gray-50"
                          >
                            <td className="p-3">{item.item_name}</td>
                            <td className="p-3 text-center font-medium">
                              {item.ordered_qty}
                            </td>
                            <td className="p-3 text-right">
                              ₹{formatCurrency(item.unit_price)}
                            </td>
                            <td className="p-3 text-right font-bold">
                              ₹{formatCurrency(item.total)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="p-4 text-center text-gray-500 italic"
                          >
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* ============================================================ */}
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setSelectedPO(null)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Close
              </button>
              {selectedPO.isSubmitMode && (
                <button
                  onClick={() => handleSubmitPO(selectedPO.id)}
                  className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-bold"
                >
                  Confirm & Submit
                </button>
              )}
              {!selectedPO.isSubmitMode && (
                <button
                  onClick={() => {
                    setSelectedPO(null);
                    navigate(`/purchase-orders/${selectedPO.id}`);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  View More
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, amount, icon, bgColor, borderColor }) {
  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  return (
    <div
      className={`p-4 rounded-xl border shadow-sm flex items-center gap-4 bg-white border-l-4 ${borderColor} transition-transform hover:scale-105 duration-200`}
    >
      <div className={`p-3 rounded-lg ${bgColor} shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs lg:text-sm text-gray-500 font-medium truncate">
          {title}
        </p>
        <h3 className="text-lg lg:text-xl font-bold text-gray-800">
          ₹{formatCurrency(amount)}
        </h3>
      </div>
    </div>
  );
}
